import React, { useMemo, useState } from 'react';
import { GoogleAdsLocationGq } from '../../../graphql/projects/models/GoogleAdsLocationGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import VoxelAutocomplete, {
  GenericOption,
  VoxelAutocompleteProps,
} from '../../voxel/Forms/VoxelAutocomplete';

export interface VoxelAutocompleteCityProps
  extends Omit<
    VoxelAutocompleteProps,
    | 'setSelectedOptions'
    | 'options'
    | 'selectedOptionValues'
    | 'fixedOptions'
    | 'freeAdd'
    | 'multi'
    | 'emptyAllowed'
    | 'placeholder'
    | 'label'
    | 'infoText'
  > {
  selectedCountry?: GoogleAdsLocationGq;
  selectedCity?: Partial<GoogleAdsLocationGq>;
  setSelectedCity: (value?: GoogleAdsLocationGq) => void;
  type: 'keyword' | 'rank' | 'both' | 'user';
}

export const VoxelAutocompleteCity: React.FC<VoxelAutocompleteCityProps> = ({
  labelPosition,
  selectedCountry,
  selectedCity,
  setSelectedCity,
  type,
  ...forwardedProps
}) => {
  const [querySearchTerm, setQuerySearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounceValue(querySearchTerm, 300);
  const query = projectsApiService.queries.googleAds.useGetLocations({
    excludeTargetType: 'Country',
    countryCode: selectedCountry?.countryCode ?? selectedCity?.countryCode,
    searchTerm: debouncedSearchTerm,
    limitItems: 50,
  });

  const locationList = useMemo(() => query?.result ?? [], [query.result, selectedCountry]);

  const locationToOption = (location: Partial<GoogleAdsLocationGq>): GenericOption => ({
    value: location.id,
    title: `${location.name}`,
    subtitle: `${location.canonicalName.replace(/,/g, ', ')} (${location.targetType})`,
    alternativeTitle: `${location.name} (${location.targetType})`,
  });
  const locationOptions: GenericOption[] = useMemo(
    () => locationList.filter((loc) => loc.id !== selectedCity?.id).map(locationToOption),
    [locationList, selectedCity]
  );

  const selectedOption: GenericOption = useMemo(() => {
    if (selectedCity) {
      return locationToOption(selectedCity);
    } else return null;
  }, [selectedCity]);

  const options: GenericOption[] = useMemo(() => {
    if (selectedOption !== null) {
      return [selectedOption, ...locationOptions];
    } else return locationOptions;
  }, [selectedOption, locationOptions]);

  const selectedOptionsValues = useMemo(
    () => (selectedOption ? [selectedOption?.value] : []),
    [selectedOption]
  );

  return (
    <VoxelAutocomplete
      {...forwardedProps}
      label={type === 'rank' ? 'Area' : 'Area'}
      labelPosition={labelPosition ?? 'left'}
      disabled={!options.length && !querySearchTerm?.length && !query?.query?.loading}
      placeholder={'none'}
      options={options}
      multi={false}
      selectedOptionValues={selectedOptionsValues}
      setSelectedOptions={(options: GenericOption[]) =>
        setSelectedCity(locationList?.find((location) => location.id === options[0]?.value))
      }
      backendSearchTerm={querySearchTerm}
      setBackendSearchTerm={setQuerySearchTerm}
      loadingOptions={query.query.loading || querySearchTerm !== debouncedSearchTerm}
    />
  );
};

export default VoxelAutocompleteCity;
